import { Component, computed, inject, input, output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { LocalizationService } from '../../../core/localization/localization.service';
import { AinFormFieldComponent } from '../../../shared/design-system/form-field.component';
import { InfoHintComponent } from '../../../shared/design-system/info-hint.component';
import {
  ComputedOutputType,
  ComputedSource,
  FormComputedExpression,
  FormField,
  FormSchemaModel,
  collectComputedDependencies,
  collectFieldReferences,
  evaluateComputed,
  fieldReferenceLabel
} from '../../../shared/dynamic-form/form-schema.models';
import { FormBuilderComputedExpressionEditorComponent } from './form-builder-computed-expression-editor.component';

interface SelectOption<T = string> {
  label: string;
  value: T;
}

@Component({
  standalone: true,
  selector: 'ain-form-builder-computed-panel',
  imports: [
    FormsModule,
    ButtonModule,
    CheckboxModule,
    InputTextModule,
    SelectModule,
    AinFormFieldComponent,
    InfoHintComponent,
    FormBuilderComputedExpressionEditorComponent
  ],
  template: `
    <section class="logic-panel">
      <ain-info-hint
        [titleKey]="'dynamic.builder.computed.infoTitle'"
        [descriptionKey]="'dynamic.builder.computed.infoDescription'"
        [bulletKeys]="[
          'dynamic.builder.computed.infoBullet',
          'dynamic.builder.computed.infoBulletFormula',
          'dynamic.builder.computed.infoBulletScript'
        ]" />

      <div class="checkbox-row">
        <p-checkbox
          binary
          [ngModel]="enabled()"
          [disabled]="!canEdit()"
          (ngModelChange)="toggleEnabled($event)" />
        <label>{{ t('dynamic.builder.computed.enable') }}</label>
      </div>

      @if (enabled()) {
        <ain-form-field [label]="t('dynamic.builder.computed.source')">
          <p-select
            [options]="sourceOptions()"
            optionLabel="label"
            optionValue="value"
            [ngModel]="source()"
            [disabled]="!canEdit()"
            appendTo="body"
            (ngModelChange)="updateSource($event)" />
        </ain-form-field>

        <ain-form-field [label]="t('dynamic.builder.computed.outputType')">
          <p-select
            [options]="outputTypeOptions()"
            optionLabel="label"
            optionValue="value"
            [ngModel]="outputType()"
            [disabled]="!canEdit()"
            appendTo="body"
            (ngModelChange)="updateOutputType($event)" />
        </ain-form-field>

        <div class="checkbox-row">
          <p-checkbox
            binary
            [ngModel]="!!field().hidden"
            [disabled]="!canEdit()"
            (ngModelChange)="updateHidden($event)" />
          <label>{{ t('dynamic.builder.computed.hideFromForm') }}</label>
        </div>

        @if (source() === 'script') {
          <ain-info-hint
            [titleKey]="'dynamic.builder.computed.scriptWarningTitle'"
            [descriptionKey]="'dynamic.builder.computed.scriptWarningDescription'"
            [bulletKeys]="['dynamic.builder.computed.scriptWarningBullet']" />

          <ain-form-field [label]="t('dynamic.builder.computed.scriptRef')">
            <p-select
              [options]="scriptOptions()"
              optionLabel="label"
              optionValue="value"
              [ngModel]="field().computed?.scriptRef ?? ''"
              [disabled]="!canEdit()"
              appendTo="body"
              (ngModelChange)="updateScriptRef($event)" />
          </ain-form-field>

          <div class="checkbox-row">
            <p-checkbox
              binary
              [ngModel]="!!field().computed?.trustClientValue"
              [disabled]="!canEdit()"
              (ngModelChange)="updateTrustClient($event)" />
            <label>{{ t('dynamic.builder.computed.trustClientValue') }}</label>
          </div>
        }

        @if (source() === 'formula' && expression(); as expr) {
          <ain-form-builder-computed-expression-editor
            [expression]="expr"
            [fieldOptions]="fieldReferenceOptions()"
            [canEdit]="canEdit()"
            (expressionChange)="updateExpression($event)" />

          <article class="preview-card">
            <strong>{{ t('dynamic.builder.computed.preview') }}</strong>
            <p>{{ previewResult() }}</p>
            <small>{{ t('dynamic.builder.computed.previewHint') }}</small>
          </article>
        }
      }
    </section>
  `,
  styles: [`
    .logic-panel { display: grid; gap: 10px; }
    .checkbox-row { display: flex; align-items: center; gap: 8px; }
    .preview-card {
      display: grid;
      gap: 4px;
      padding: 10px;
      border: 1px solid #dbeafe;
      border-radius: 8px;
      background: #eff6ff;
    }
    .preview-card p {
      margin: 0;
      font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
      word-break: break-word;
    }
  `]
})
export class FormBuilderComputedPanelComponent {
  private readonly localization = inject(LocalizationService);

  readonly field = input.required<FormField>();
  readonly schema = input.required<FormSchemaModel>();
  readonly culture = input.required<string>();
  readonly canEdit = input(true);

  readonly fieldChange = output<FormField>();

  readonly enabled = computed(() => !!this.field().computed || this.field().type === 'computed');
  readonly expression = computed(() => this.field().computed?.structured);
  readonly source = computed<ComputedSource>(() =>
    ((this.field().computed?.source ?? 'formula') as ComputedSource)
  );
  readonly outputType = computed<ComputedOutputType>(() =>
    ((this.field().computed?.outputType ?? 'number') as ComputedOutputType)
  );

  readonly fieldReferenceOptions = computed(() =>
    collectFieldReferences(this.schema(), this.field().key).map(field => ({
      label: fieldReferenceLabel(field, this.culture()),
      value: field.key
    }))
  );

  readonly scriptOptions = computed(() =>
    (this.schema().scripts ?? [])
      .filter(script => !!script.ref)
      .map(script => ({ label: script.ref, value: script.ref }))
  );

  readonly sourceOptions = computed<SelectOption<ComputedSource>[]>(() => [
    { label: this.t('dynamic.builder.computed.source.formula'), value: 'formula' },
    { label: this.t('dynamic.builder.computed.source.script'), value: 'script' }
  ]);

  readonly outputTypeOptions = computed<SelectOption<ComputedOutputType>[]>(() => [
    { label: this.t('dynamic.builder.computed.outputType.number'), value: 'number' },
    { label: this.t('dynamic.builder.computed.outputType.string'), value: 'string' },
    { label: this.t('dynamic.builder.computed.outputType.boolean'), value: 'boolean' },
    { label: this.t('dynamic.builder.computed.outputType.json'), value: 'json' }
  ]);

  readonly previewResult = computed(() => {
    const structured = this.expression();
    if (!structured) {
      return '—';
    }
    const sample: Record<string, unknown> = {};
    for (const field of collectFieldReferences(this.schema(), this.field().key)) {
      if (field.type === 'number' || field.type === 'nps' || field.type === 'rating') {
        sample[field.key] = 1;
      } else if (field.type === 'boolean') {
        sample[field.key] = true;
      } else if (field.options?.length) {
        sample[field.key] = field.options[0].value;
      } else {
        sample[field.key] = 'sample';
      }
    }
    const result = evaluateComputed(structured, sample);
    if (result === undefined || result === null) {
      return '—';
    }
    return typeof result === 'object' ? JSON.stringify(result) : String(result);
  });

  toggleEnabled(value: boolean): void {
    if (!value) {
      this.fieldChange.emit({
        ...this.field(),
        computed: undefined,
        type: this.field().type === 'computed' ? 'number' : this.field().type,
        readonly: this.field().type === 'computed' ? false : this.field().readonly
      });
      return;
    }
    const structured: FormComputedExpression = {
      operation: 'sum',
      operands: [{ kind: 'const', value: 0 }]
    };
    this.emitComputed({
      source: 'formula',
      outputType: 'number',
      structured,
      dependsOn: collectComputedDependencies(structured)
    }, true);
  }

  updateSource(source: ComputedSource): void {
    const current = this.field().computed ?? {};
    if (source === 'formula') {
      const structured = current.structured ?? { operation: 'sum', operands: [{ kind: 'const', value: 0 }] };
      this.emitComputed({
        ...current,
        source,
        structured,
        dependsOn: collectComputedDependencies(structured),
        scriptRef: undefined,
        trustClientValue: false
      }, true);
      return;
    }
    this.emitComputed({
      ...current,
      source,
      scriptRef: current.scriptRef ?? this.scriptOptions()[0]?.value ?? '',
      trustClientValue: false
    }, true);
  }

  updateOutputType(outputType: ComputedOutputType): void {
    this.emitComputed({ ...(this.field().computed ?? {}), outputType }, true);
  }

  updateHidden(hidden: boolean): void {
    this.fieldChange.emit({ ...this.field(), hidden });
  }

  updateScriptRef(scriptRef: string): void {
    this.emitComputed({ ...(this.field().computed ?? {}), source: 'script', scriptRef }, true);
  }

  updateTrustClient(trustClientValue: boolean): void {
    this.emitComputed({ ...(this.field().computed ?? {}), source: 'script', trustClientValue }, true);
  }

  updateExpression(structured: FormComputedExpression): void {
    this.emitComputed({
      ...(this.field().computed ?? {}),
      source: 'formula',
      structured,
      dependsOn: collectComputedDependencies(structured)
    }, true);
  }

  private emitComputed(
    computed: NonNullable<FormField['computed']>,
    forceComputedType = false
  ): void {
    this.fieldChange.emit({
      ...this.field(),
      type: forceComputedType || this.field().type === 'computed' ? 'computed' : this.field().type,
      readonly: true,
      computed
    });
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
